// /shared/<guid>: a record someone shared publicly. No sign-in: the record panel's header, tabs and sections, with
// nothing to open beside them (other records and budget lines read as text; documents open at their source). The
// server leaves out everything about the team that shared it.
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { api, errorText } from "../api/client";
import type { SharedRecord as Shared } from "../api/types";
import { Badge } from "../components/Badge";
import { SoleChip } from "../components/Rows";
import { fmt, orgName, titleCase } from "../lib/format";
import { listHint, otherLinks, RecordTab, TabBar, tabsFor, When, type Tab } from "./NoticePanel";

const NO_TERMS = { terms: [] };

export function SharedRecord() {
  const { token = "" } = useParams();
  const [picked, setPicked] = useState<Tab>("overview");
  const record = useQuery({
    queryKey: ["shared", token],
    queryFn: () => api<Shared>(`/api/shared/${encodeURIComponent(token)}`),
    retry: false,
  });
  const n = record.data;
  useEffect(() => {
    // Shared links are for the people they're sent to, not search engines (the API says so too).
    const robots = Object.assign(document.createElement("meta"), { name: "robots", content: "noindex" });
    document.head.append(robots);
    return () => robots.remove();
  }, []);
  useEffect(() => {
    document.title = n ? `${titleCase(n.title)} · GovChew` : "GovChew";
  }, [n]);

  return (
    <div className="shared-page">
      <header className="shared-top">
        <a href="/" className="brand">
          <img src="/brand/govchew-logo.svg" height={28} alt="GovChew" />
        </a>
        <a className="btn small" href="/">
          Sign in
        </a>
      </header>
      <main className="shared-card">
        {record.isPending ? (
          <p className="empty">Loading…</p>
        ) : record.isError || !n ? (
          <div className="shared-gone">
            <h1>This link isn’t available</h1>
            <p>{errorText(record.error, "This record is no longer available.")}</p>
          </div>
        ) : (
          <Record n={n} picked={picked} pick={setPicked} />
        )}
      </main>
    </div>
  );
}

function Record({ n, picked, pick }: { n: Shared; picked: Tab; pick: (t: Tab) => void }) {
  const org = orgName(n.agency);
  const files = n.files.map((f) => ({ ...f, size: null, posted: null }));
  const docs = otherLinks(n, files);
  const fileCount = files.length + docs.length;
  const tabs = tabsFor(n, fileCount);
  const tab = tabs.includes(picked) ? picked : "overview";
  return (
    <>
      <div className="np-head">
        <div className="p-org" title={org.full}>
          <Badge agency={n.agency} small />
          <span>{org.full || "Unknown buyer"}</span>
        </div>
        <h1>{titleCase(n.title)}</h1>
        <div className="np-status">
          <When n={n} />
          <span>{[n.kind, n.who_label || "Open to anyone"].filter(Boolean).join(" · ")}</span>
          {n.sole_source ? <SoleChip /> : null}
        </div>
        {/^https?:\/\//i.test(n.url) ? (
          <div className="p-actions">
            <a className="btn primary" href={n.url} target="_blank" rel="noopener noreferrer" title={n.url_is_list ? listHint(n.title) : undefined}>
              {n.url_is_list ? "Open the list it’s on ↗" : "Open the original notice ↗"}
            </a>
          </div>
        ) : null}
        <TabBar n={n} tabs={tabs} tab={tab} fileCount={fileCount} go={pick} />
      </div>
      <div className="np-body" id="p-body" role={tabs.length > 1 ? "tabpanel" : undefined} aria-labelledby={tabs.length > 1 ? `tab-${tab}` : undefined}>
        <RecordTab tab={tab} n={n} org={org} cite={NO_TERMS} go={pick} opens={{}} files={files} docs={docs} />
      </div>
      <p className="shared-foot">Shared from GovChew on {fmt(n.shared_at.slice(0, 10), { month: "long", day: "numeric", year: "numeric" })}.</p>
    </>
  );
}
